import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Table, Modal, Form, Badge, Alert } from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";
import PackageService, { Package, PaymentDetails, PaymentTransaction } from "../services/PackageService";
import { apiGet } from "../services/BaseService";
import { isMobileNumber, formatPhoneNumber, getWhatsAppUrl, getPhoneCallUrl } from "../utils/phoneUtils";

const ManagePackagePage: React.FC = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [paymentRequests, setPaymentRequests] = useState<PaymentTransaction[]>([]);
  const [paymentHistory, setPaymentHistory] = useState<PaymentTransaction[]>([]);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [activeTab, setActiveTab] = useState<'packages' | 'payments' | 'history' | 'settings' | 'custom-settings'>('packages');
  
  // Search filters
  const [countries, setCountries] = useState<{countryCode: string, name: string}[]>([]);
  const [states, setStates] = useState<{stateCode: string, name: string}[]>([]);
  const [groups, setGroups] = useState<{_id: string, name: string}[]>([]);
  const [searchFilters, setSearchFilters] = useState({
    country: '',
    state: '',
    group: ''
  });
  const [requestsCurrentPage, setRequestsCurrentPage] = useState(1);
  const [requestsTotalPages, setRequestsTotalPages] = useState(1);
  const [historyFilters, setHistoryFilters] = useState({
    country: '',
    state: '',
    group: '',
    status: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Package form state
  const [packageForm, setPackageForm] = useState({
    packageName: '',
    scheduleCount: 1,
    perScheduleBasePrice: 100,
    ticketCountPerSchedule: 50,
    perTicketBasePrice: 2,
    isRestricted: false
  });

  // Payment details form state
  const [paymentForm, setPaymentForm] = useState({
    upiId: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountHolderName: ''
  });

  // Custom package settings state
  const [customSettings, setCustomSettings] = useState({
    perScheduleBasePrice: 100,
    perTicketBasePrice: 2
  });
  const [showCustomSettingsModal, setShowCustomSettingsModal] = useState(false);

  useEffect(() => {
    fetchPackages();
    fetchPaymentRequests();
    fetchPaymentDetails();
    fetchCustomPackageSettings();
    fetchCountries();
    fetchGroups();
  }, []);

  useEffect(() => {
    if (searchFilters.country) {
      fetchStates(searchFilters.country);
    } else {
      setStates([]);
    }
  }, [searchFilters.country]);

  useEffect(() => {
    if (historyFilters.country) {
      fetchStates(historyFilters.country);
    } else {
      setStates([]);
    }
  }, [historyFilters.country]);

  useEffect(() => {
    if (activeTab === 'payments') {
      fetchPaymentRequests();
    }
  }, [activeTab, searchFilters, requestsCurrentPage]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchPaymentHistory();
    }
  }, [activeTab, historyFilters, currentPage]);

  const fetchPackages = async () => {
    try {
      const data = await PackageService.getAllPackages();
      setPackages(data);
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const fetchPaymentRequests = async () => {
    try {
      const params = new URLSearchParams();
      if (searchFilters.country) params.append('country', searchFilters.country);
      if (searchFilters.state) params.append('state', searchFilters.state);
      if (searchFilters.group) params.append('group', searchFilters.group);
      params.append('page', requestsCurrentPage.toString());
      params.append('limit', itemsPerPage.toString());
      
      const data = await PackageService.getPendingPayments(params.toString());
      setPaymentRequests(data.requests || []);
      setRequestsTotalPages(Math.ceil((data.total || 0) / itemsPerPage));
    } catch (error) {
      console.error('Error fetching payment requests:', error);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const params = new URLSearchParams();
      if (historyFilters.country) params.append('country', historyFilters.country);
      if (historyFilters.state) params.append('state', historyFilters.state);
      if (historyFilters.group) params.append('group', historyFilters.group);
      if (historyFilters.status) params.append('status', historyFilters.status);
      params.append('page', currentPage.toString());
      params.append('limit', itemsPerPage.toString());
      
      const data = await PackageService.getPaymentHistory(params.toString());
      setPaymentHistory(data.transactions || []);
      setTotalPages(Math.ceil((data.total || 0) / itemsPerPage));
    } catch (error) {
      console.error('Error fetching payment history:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const data = await PackageService.getCountries();
      setCountries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountries([]);
    }
  };

  const fetchStates = async (countryCode: string) => {
    try {
      const data = await PackageService.getStates(countryCode);
      setStates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    }
  };

  const fetchGroups = async (countryCode?: string, stateCode?: string) => {
    try {
      let url = '/groups';
      const params = new URLSearchParams();
      if (countryCode) params.append('country', countryCode);
      if (stateCode) params.append('state', stateCode);
      
      if (params.toString()) {
        url = `/groups/filter?${params.toString()}`;
      }
      
      const data = await apiGet(url);
      setGroups(Array.isArray(data.groups || data) ? (data.groups || data) : []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    }
  };

  const fetchPaymentDetails = async () => {
    try {
      const data = await PackageService.getPaymentDetails();
      setPaymentDetails(data);
      setPaymentForm({
        upiId: data.upiId || '',
        bankName: data.bankName || '',
        accountNumber: data.accountNumber || '',
        ifscCode: data.ifscCode || '',
        accountHolderName: data.accountHolderName || ''
      });
    } catch (error) {
      console.error('Error fetching payment details:', error);
    }
  };

  const fetchCustomPackageSettings = async () => {
    try {
      const data = await PackageService.getCustomPackageSettings();
      setCustomSettings({
        perScheduleBasePrice: data.perScheduleBasePrice || 100,
        perTicketBasePrice: data.perTicketBasePrice || 2
      });
    } catch (error) {
      console.error('Error fetching custom package settings:', error);
    }
  };

  const handlePackageSubmit = async () => {
    try {
      if (editingPackage) {
        await PackageService.updatePackage(editingPackage._id, packageForm);
      } else {
        await PackageService.createPackage(packageForm);
      }
      
      setShowPackageModal(false);
      setEditingPackage(null);
      resetPackageForm();
      fetchPackages();
    } catch (error) {
      console.error('Error saving package:', error);
    }
  };

  const handlePaymentDetailsSubmit = async () => {
    try {
      await PackageService.setPaymentDetails(paymentForm);
      setShowPaymentModal(false);
      fetchPaymentDetails();
    } catch (error) {
      console.error('Error saving payment details:', error);
    }
  };

  const handleCustomSettingsSubmit = async () => {
    try {
      await PackageService.setCustomPackageSettings(customSettings.perScheduleBasePrice, customSettings.perTicketBasePrice);
      setShowCustomSettingsModal(false);
      fetchCustomPackageSettings();
    } catch (error) {
      console.error('Error saving custom package settings:', error);
    }
  };

  const handleApprovePayment = async (transactionId: string) => {
    try {
      await PackageService.approvePayment(transactionId);
      
      // Send approval notification
      const request = paymentRequests.find(r => r._id === transactionId);
      if (request) {
        await sendPackageNotification('payment_approved', request);
      }
      
      fetchPaymentRequests();
    } catch (error) {
      console.error('Error approving payment:', error);
    }
  };

  const handleRejectPayment = async (transactionId: string) => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      try {
        await PackageService.rejectPayment(transactionId, reason);
        
        // Send rejection notification
        const request = paymentRequests.find(r => r._id === transactionId);
        if (request) {
          await sendPackageNotification('payment_rejected', request, reason);
        }
        
        fetchPaymentRequests();
      } catch (error) {
        console.error('Error rejecting payment:', error);
      }
    }
  };

  const handleEditPackage = (pkg: Package) => {
    setEditingPackage(pkg);
    setPackageForm({
      packageName: pkg.packageName,
      scheduleCount: pkg.scheduleCount,
      perScheduleBasePrice: pkg.perScheduleBasePrice,
      ticketCountPerSchedule: pkg.ticketCountPerSchedule,
      perTicketBasePrice: pkg.perTicketBasePrice,
      isRestricted: pkg.isRestricted
    });
    setShowPackageModal(true);
  };

  const handleDeletePackage = async (packageId: string) => {
    if (window.confirm('Are you sure you want to delete this package?')) {
      try {
        await PackageService.deletePackage(packageId);
        fetchPackages();
      } catch (error) {
        console.error('Error deleting package:', error);
      }
    }
  };

  const resetPackageForm = () => {
    setPackageForm({
      packageName: '',
      scheduleCount: 1,
      perScheduleBasePrice: 100,
      ticketCountPerSchedule: 50,
      perTicketBasePrice: 2,
      isRestricted: false
    });
  };

  const calculateTotalPrice = () => {
    return (packageForm.scheduleCount * packageForm.perScheduleBasePrice) + 
           (packageForm.scheduleCount * packageForm.ticketCountPerSchedule * packageForm.perTicketBasePrice);
  };

  const sendPackageNotification = async (action: string, request: any, rejectionReason?: string) => {
    try {
      const requestBody = {
        action,
        packageData: {
          groupName: (request as any).groupId?.name || 'Group',
          packageName: (request as any).packageId?.packageName || 'Package',
          amount: request.amount,
          transactionId: request._id,
          rejectionReason
        },
        userData: {
          name: (request as any).userId?.firstName || 'User',
          email: (request as any).userId?.email
        }
      };
      
      const response = await fetch('/api/packages/communication-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      const data = await response.json();
      const { links } = data;
      
      // Open WhatsApp with pre-filled message
      if (links?.whatsapp && (request as any).userId?.phone) {
        window.open(links.whatsapp.link, '_blank');
      }
    } catch (error) {
      console.error('Error sending package notification:', error);
    }
  };

  const sendAdminNotification = async (action: string, request: any) => {
    try {
      const requestBody = {
        action,
        packageData: {
          groupName: (request as any).groupId?.name || 'Group',
          packageName: (request as any).packageId?.packageName || 'Package',
          amount: request.amount,
          transactionId: request._id
        },
        userData: {
          name: (request as any).userId?.firstName || 'User',
          email: (request as any).userId?.email
        }
      };
      
      const response = await fetch('/api/packages/communication-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      const data = await response.json();
      const { links } = data;
      
      // Show communication options to admin
      if (links?.email) {
        window.open(links.email.link, '_blank');
      }
    } catch (error) {
      console.error('Error getting admin notification links:', error);
    }
  };

  return (
    <PermissionWrapper pageName="ManagePackagePage">
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <h2>
              <i className="bi bi-box-seam me-2"></i>
              Package Management
            </h2>
          </Col>
        </Row>

        {/* Navigation Tabs */}
        <Row className="mb-4">
          <Col>
            <div className="btn-group" role="group">
              <Button 
                variant={activeTab === 'packages' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveTab('packages')}
              >
                Packages
              </Button>
              <Button 
                variant={activeTab === 'payments' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveTab('payments')}
              >
                Payment Requests ({paymentRequests.length})
              </Button>
              <Button 
                variant={activeTab === 'history' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveTab('history')}
              >
                Payment History
              </Button>
              <Button 
                variant={activeTab === 'settings' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveTab('settings')}
              >
                Payment Settings
              </Button>
              <Button 
                variant={activeTab === 'custom-settings' ? 'primary' : 'outline-primary'}
                onClick={() => setActiveTab('custom-settings')}
              >
                Custom Package Settings
              </Button>
            </div>
          </Col>
        </Row>

        {/* Packages Tab */}
        {activeTab === 'packages' && (
          <Row>
            <Col>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5>Packages</h5>
                  <Button 
                    variant="primary"
                    onClick={() => {
                      resetPackageForm();
                      setEditingPackage(null);
                      setShowPackageModal(true);
                    }}
                  >
                    <i className="bi bi-plus me-2"></i>
                    Add Package
                  </Button>
                </Card.Header>
                <Card.Body>
                  <Row>
                    {packages.map((pkg) => (
                      <Col key={pkg._id} xs={12} md={6} lg={4} className="mb-3">
                        <Card className="h-100">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <h6 className="card-title mb-0">{pkg.packageName}</h6>
                              <div>
                                <Badge bg={pkg.isCustom ? 'info' : 'secondary'} className="me-1">
                                  {pkg.isCustom ? 'Custom' : 'Predefined'}
                                </Badge>
                                {pkg.isRestricted && (
                                  <Badge bg="warning">Restricted</Badge>
                                )}
                              </div>
                            </div>
                            
                            <div className="mb-2">
                              <small className="text-muted d-block">
                                <i className="bi bi-calendar me-1"></i>
                                {pkg.scheduleCount} Schedules
                              </small>
                              <small className="text-muted d-block">
                                <i className="bi bi-ticket me-1"></i>
                                {pkg.ticketCountPerSchedule} Tickets/Schedule
                              </small>
                            </div>
                            
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h5 className="text-primary mb-0">₹{pkg.totalPrice}</h5>
                              <Badge bg={pkg.isActive ? 'success' : 'danger'}>
                                {pkg.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                            
                            <div className="d-grid gap-2">
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => handleEditPackage(pkg)}
                              >
                                <i className="bi bi-pencil me-1"></i>
                                Edit
                              </Button>
                              <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => handleDeletePackage(pkg._id)}
                              >
                                <i className="bi bi-trash me-1"></i>
                                Delete
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                  {packages.length === 0 && (
                    <div className="text-center py-4">
                      <i className="bi bi-box-seam display-4 text-muted"></i>
                      <p className="text-muted mt-2">No packages found</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Payment Requests Tab */}
        {activeTab === 'payments' && (
          <>
            {/* Search Filters */}
            <Row className="mb-3">
              <Col>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Search Payment Requests</h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Country</Form.Label>
                          <Form.Select 
                            value={searchFilters.country} 
                            onChange={(e) => {
                              const country = e.target.value;
                              setSearchFilters({...searchFilters, country, state: '', group: ''});
                              setRequestsCurrentPage(1);
                              if (country) {
                                fetchStates(country);
                                fetchGroups(country);
                              } else {
                                setStates([]);
                                setGroups([]);
                              }
                            }}
                          >
                            <option value="">All Countries</option>
                            {countries.map(country => (
                              <option key={country.countryCode} value={country.countryCode}>{country.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>State</Form.Label>
                          <Form.Select 
                            value={searchFilters.state} 
                            onChange={(e) => {
                              const state = e.target.value;
                              setSearchFilters({...searchFilters, state, group: ''});
                              setRequestsCurrentPage(1);
                              if (state && searchFilters.country) {
                                fetchGroups(searchFilters.country, state);
                              } else if (searchFilters.country) {
                                fetchGroups(searchFilters.country);
                              }
                            }}
                            disabled={!searchFilters.country}
                          >
                            <option value="">All States</option>
                            {states.map(state => (
                              <option key={state.stateCode} value={state.stateCode}>{state.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Group</Form.Label>
                          <Form.Select 
                            value={searchFilters.group} 
                            onChange={(e) => {
                              setSearchFilters({...searchFilters, group: e.target.value});
                              setRequestsCurrentPage(1);
                            }}
                          >
                            <option value="">All Groups</option>
                            {groups.map(group => (
                              <option key={group._id} value={group._id}>{group.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3} className="d-flex align-items-end">
                        <Button 
                          variant="outline-secondary" 
                          onClick={() => {
                            setSearchFilters({country: '', state: '', group: ''});
                            setRequestsCurrentPage(1);
                            setStates([]);
                            fetchGroups();
                          }}
                          className="w-100"
                        >
                          Clear Filters
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            <Row>
              <Col>
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5>Payment Requests</h5>
                    <Badge bg="warning" className="fs-6">
                      {paymentRequests.length} Pending
                    </Badge>
                  </Card.Header>
                <Card.Body>
                  {paymentRequests.length > 0 ? (
                    <Row>
                      {paymentRequests.map((request) => (
                        <Col key={request._id} xs={12} md={6} className="mb-3">
                          <Card className="h-100 border-start border-warning border-3">
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="card-title mb-0">
                                  {(request as any).groupId?.name || 'N/A'}
                                </h6>
                                <Badge bg={request.status === 'pending' ? 'warning' : request.status === 'verification-pending' ? 'info' : 'secondary'}>
                                  {request.status.replace('-', ' ').toUpperCase()}
                                </Badge>
                              </div>
                              
                              <div className="mb-2">
                                <small className="text-muted d-block">
                                  <i className="bi bi-box me-1"></i>
                                  <strong>Package:</strong> {(request as any).packageId?.packageName || 'N/A'}
                                </small>
                                <small className="text-muted d-block">
                                  <i className="bi bi-credit-card me-1"></i>
                                  <strong>Payment:</strong> {request.paymentMethod.toUpperCase()}
                                </small>
                                <small className="text-muted d-block">
                                  <i className="bi bi-calendar me-1"></i>
                                  <strong>Date:</strong> {new Date(request.createdAt).toLocaleDateString()}
                                </small>
                                {request.transactionId && (
                                  <small className="text-muted d-block">
                                    <i className="bi bi-receipt me-1"></i>
                                    <strong>Transaction:</strong> {request.transactionId}
                                  </small>
                                )}
                              </div>
                              
                              <div className="mb-2">
                                <h5 className="text-success mb-1">₹{request.amount}</h5>
                                {/* Phone Number with Quick Actions */}
                                {(request as any).userId?.phone && (
                                  <div className="d-flex align-items-center gap-2 mt-2">
                                    <small className="text-muted">
                                      <i className="bi bi-telephone me-1"></i>
                                      {formatPhoneNumber((request as any).userId.phone)}
                                    </small>
                                    <div className="d-flex gap-1">
                                      {isMobileNumber((request as any).userId.phone) && (
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          href={getPhoneCallUrl((request as any).userId.phone)}
                                          title="Call User"
                                          style={{ padding: '2px 6px' }}
                                        >
                                          <i className="bi bi-telephone"></i>
                                        </Button>
                                      )}
                                      <Button
                                        variant="outline-success"
                                        size="sm"
                                        href={getWhatsAppUrl((request as any).userId.phone, `Hi ${(request as any).userId?.firstName || 'User'}, regarding your payment request for ${(request as any).packageId?.packageName} (₹${request.amount}). Transaction ID: ${request._id}`)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="WhatsApp User"
                                        style={{ padding: '2px 6px' }}
                                      >
                                        <i className="bi bi-whatsapp"></i>
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              <div className="d-grid gap-2">
                                <div className="d-flex gap-1 mb-2">
                                  <Button 
                                    variant="success" 
                                    size="sm"
                                    onClick={() => handleApprovePayment(request._id)}
                                    className="flex-fill"
                                  >
                                    <i className="bi bi-check-circle me-1"></i>
                                    Approve
                                  </Button>
                                  <Button 
                                    variant="danger" 
                                    size="sm"
                                    onClick={() => handleRejectPayment(request._id)}
                                    className="flex-fill"
                                  >
                                    <i className="bi bi-x-circle me-1"></i>
                                    Reject
                                  </Button>
                                </div>
                                
                                <div className="d-flex gap-1 mb-2">
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => sendPackageNotification('payment_request', request)}
                                    className="flex-fill"
                                    title="Send WhatsApp Reminder"
                                  >
                                    <i className="bi bi-whatsapp me-1"></i>
                                    WhatsApp
                                  </Button>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => sendAdminNotification('admin_notification', request)}
                                    className="flex-fill"
                                    title="Send Email Notification"
                                  >
                                    <i className="bi bi-envelope me-1"></i>
                                    Email
                                  </Button>
                                </div>
                                
                                {/* User Communication Buttons */}
                                {(request as any).userId?.phone && (
                                  <div className="mt-2 pt-2 border-top">
                                    <h6 className="text-info mb-2">Contact User</h6>
                                    <div className="d-flex gap-1 mb-2">
                                      {isMobileNumber((request as any).userId.phone) && (
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          href={getPhoneCallUrl((request as any).userId.phone)}
                                          title={`Call User ${formatPhoneNumber((request as any).userId.phone)}`}
                                          className="flex-fill"
                                        >
                                          <i className="bi bi-telephone me-1"></i>
                                          Call User
                                        </Button>
                                      )}
                                      <Button
                                        variant="outline-success"
                                        size="sm"
                                        href={getWhatsAppUrl((request as any).userId.phone, `Hi ${(request as any).userId?.firstName || 'User'}, regarding your payment request for ${(request as any).packageId?.packageName} (₹${request.amount}). Transaction ID: ${request._id}`)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={`WhatsApp User ${formatPhoneNumber((request as any).userId.phone)}`}
                                        className="flex-fill"
                                      >
                                        <i className="bi bi-whatsapp me-1"></i>
                                        WhatsApp User
                                      </Button>
                                    </div>
                                    {(request as any).userId?.email && (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        href={`mailto:${(request as any).userId.email}?subject=Payment Request - ${(request as any).packageId?.packageName}&body=Hi ${(request as any).userId?.firstName || 'User'}, regarding your payment request for ${(request as any).packageId?.packageName} (₹${request.amount}). Transaction ID: ${request._id}`}
                                        className="w-100"
                                      >
                                        <i className="bi bi-envelope me-1"></i>
                                        Email User
                                      </Button>
                                    )}
                                  </div>
                                )}
                                
                                {/* User Contact Info */}
                                <div className="mt-2 pt-2 border-top">
                                  <h6 className="text-primary mb-2">Contact Details</h6>
                                  {(request as any).userId && (
                                    <>
                                      <small className="text-muted d-block mb-1">
                                        <i className="bi bi-person me-1"></i>
                                        <strong>User:</strong> {(request as any).userId?.firstName} {(request as any).userId?.lastName}
                                      </small>
                                      {(request as any).userId?.phone && (
                                        <small className="text-muted d-block mb-1">
                                          <i className="bi bi-telephone me-1"></i>
                                          <strong>Phone:</strong> {formatPhoneNumber((request as any).userId.phone)}
                                        </small>
                                      )}
                                      {(request as any).userId?.email && (
                                        <small className="text-muted d-block mb-1">
                                          <i className="bi bi-envelope me-1"></i>
                                          <strong>Email:</strong> {(request as any).userId.email}
                                        </small>
                                      )}
                                    </>
                                  )}
                                  {(request as any).groupId && (
                                    <>
                                      <small className="text-muted d-block mb-1">
                                        <i className="bi bi-people me-1"></i>
                                        <strong>Group:</strong> {(request as any).groupId?.name}
                                      </small>
                                      {(request as any).groupId?.phone && (
                                        <small className="text-muted d-block mb-1">
                                          <i className="bi bi-telephone-fill me-1"></i>
                                          <strong>Group Phone:</strong> {formatPhoneNumber((request as any).groupId.phone)}
                                        </small>
                                      )}
                                      {(request as any).groupId?.email && (
                                        <small className="text-muted d-block mb-1">
                                          <i className="bi bi-envelope-fill me-1"></i>
                                          <strong>Group Email:</strong> {(request as any).groupId.email}
                                        </small>
                                      )}
                                    </>
                                  )}
                                </div>
                                
                                {/* Additional Communication Buttons for Group */}
                                {(request as any).groupId?.phone && (
                                  <div className="mt-2 pt-2 border-top">
                                    <h6 className="text-success mb-2">Contact Group</h6>
                                    <div className="d-flex gap-1 mb-2">
                                      {isMobileNumber((request as any).groupId.phone) && (
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          href={getPhoneCallUrl((request as any).groupId.phone)}
                                          title={`Call Group ${formatPhoneNumber((request as any).groupId.phone)}`}
                                          className="flex-fill"
                                        >
                                          <i className="bi bi-telephone me-1"></i>
                                          Call Group
                                        </Button>
                                      )}
                                      <Button
                                        variant="outline-success"
                                        size="sm"
                                        href={getWhatsAppUrl((request as any).groupId.phone, `Hi ${(request as any).groupId?.name}, regarding payment request for ${(request as any).packageId?.packageName} (₹${request.amount}). Transaction ID: ${request._id}`)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={`WhatsApp Group ${formatPhoneNumber((request as any).groupId.phone)}`}
                                        className="flex-fill"
                                      >
                                        <i className="bi bi-whatsapp me-1"></i>
                                        WhatsApp Group
                                      </Button>
                                    </div>
                                    {(request as any).groupId?.email && (
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        href={`mailto:${(request as any).groupId.email}?subject=Payment Request - ${(request as any).packageId?.packageName}&body=Hi ${(request as any).groupId?.name}, regarding your payment request for ${(request as any).packageId?.packageName} (₹${request.amount}). Transaction ID: ${request._id}`}
                                        className="w-100"
                                      >
                                        <i className="bi bi-envelope me-1"></i>
                                        Email Group
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <div className="text-center py-4">
                      <i className="bi bi-credit-card display-4 text-muted"></i>
                      <p className="text-muted mt-2">No pending payment requests</p>
                    </div>
                  )}
                  
                  {requestsTotalPages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                      <nav>
                        <ul className="pagination">
                          <li className={`page-item ${requestsCurrentPage === 1 ? 'disabled' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => setRequestsCurrentPage(requestsCurrentPage - 1)}
                              disabled={requestsCurrentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {[...Array(requestsTotalPages)].map((_, i) => (
                            <li key={i + 1} className={`page-item ${requestsCurrentPage === i + 1 ? 'active' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => setRequestsCurrentPage(i + 1)}
                              >
                                {i + 1}
                              </button>
                            </li>
                          ))}
                          <li className={`page-item ${requestsCurrentPage === requestsTotalPages ? 'disabled' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => setRequestsCurrentPage(requestsCurrentPage + 1)}
                              disabled={requestsCurrentPage === requestsTotalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
          </>
        )}

        {/* Payment History Tab */}
        {activeTab === 'history' && (
          <>
            {/* Search Filters for History */}
            <Row className="mb-3">
              <Col>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Search Payment History</h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Country</Form.Label>
                          <Form.Select 
                            value={historyFilters.country} 
                            onChange={(e) => {
                              const country = e.target.value;
                              setHistoryFilters({...historyFilters, country, state: '', group: ''});
                              setCurrentPage(1);
                              if (country) {
                                fetchStates(country);
                                fetchGroups(country);
                              } else {
                                setStates([]);
                                setGroups([]);
                              }
                            }}
                          >
                            <option value="">All Countries</option>
                            {countries.map(country => (
                              <option key={country.countryCode} value={country.countryCode}>{country.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>State</Form.Label>
                          <Form.Select 
                            value={historyFilters.state} 
                            onChange={(e) => {
                              const state = e.target.value;
                              setHistoryFilters({...historyFilters, state, group: ''});
                              setCurrentPage(1);
                              if (state && historyFilters.country) {
                                fetchGroups(historyFilters.country, state);
                              } else if (historyFilters.country) {
                                fetchGroups(historyFilters.country);
                              }
                            }}
                            disabled={!historyFilters.country}
                          >
                            <option value="">All States</option>
                            {states.map(state => (
                              <option key={state.stateCode} value={state.stateCode}>{state.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Group</Form.Label>
                          <Form.Select 
                            value={historyFilters.group} 
                            onChange={(e) => {
                              setHistoryFilters({...historyFilters, group: e.target.value});
                              setCurrentPage(1);
                            }}
                          >
                            <option value="">All Groups</option>
                            {groups.map(group => (
                              <option key={group._id} value={group._id}>{group.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Status</Form.Label>
                          <Form.Select 
                            value={historyFilters.status} 
                            onChange={(e) => {
                              setHistoryFilters({...historyFilters, status: e.target.value});
                              setCurrentPage(1);
                            }}
                          >
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="verification-pending">Verification Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3} className="d-flex align-items-end">
                        <Button 
                          variant="outline-secondary" 
                          onClick={() => {
                            setHistoryFilters({country: '', state: '', group: '', status: ''});
                            setCurrentPage(1);
                            setStates([]);
                            fetchGroups();
                          }}
                          className="w-100"
                        >
                          Clear Filters
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            <Row>
              <Col>
                <Card>
                  <Card.Header>
                    <h5>Payment History</h5>
                  </Card.Header>
                  <Card.Body>
                    {paymentHistory.length > 0 ? (
                      <>
                        <Table responsive striped hover>
                          <thead>
                            <tr>
                              <th>Group</th>
                              <th>Package</th>
                              <th>Amount</th>
                              <th>Payment Method</th>
                              <th>Status</th>
                              <th>Date</th>
                              <th>Transaction ID</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paymentHistory.map((transaction) => (
                              <tr key={transaction._id}>
                                <td>{(transaction as any).groupId?.name || 'N/A'}</td>
                                <td>{(transaction as any).packageId?.packageName || 'N/A'}</td>
                                <td>₹{transaction.amount}</td>
                                <td>
                                  <Badge bg="secondary">
                                    {transaction.paymentMethod.toUpperCase()}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge bg={
                                    transaction.status === 'approved' ? 'success' :
                                    transaction.status === 'rejected' ? 'danger' :
                                    transaction.status === 'verification-pending' ? 'info' : 'warning'
                                  }>
                                    {transaction.status.replace('-', ' ').toUpperCase()}
                                  </Badge>
                                </td>
                                <td>{new Date(transaction.createdAt).toLocaleDateString()}</td>
                                <td>
                                  <small className="text-muted">
                                    {transaction.transactionId || transaction._id.slice(-8)}
                                  </small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        
                        {totalPages > 1 && (
                          <div className="d-flex justify-content-center mt-3">
                            <nav>
                              <ul className="pagination">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                  >
                                    Previous
                                  </button>
                                </li>
                                {[...Array(totalPages)].map((_, i) => (
                                  <li key={i + 1} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                    <button 
                                      className="page-link" 
                                      onClick={() => setCurrentPage(i + 1)}
                                    >
                                      {i + 1}
                                    </button>
                                  </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                  >
                                    Next
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <i className="bi bi-clock-history display-4 text-muted"></i>
                        <p className="text-muted mt-2">No payment history found</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

        {/* Payment Settings Tab */}
        {activeTab === 'settings' && (
          <Row>
            <Col>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5>Payment Settings</h5>
                  <Button 
                    variant="primary"
                    onClick={() => setShowPaymentModal(true)}
                  >
                    <i className="bi bi-gear me-2"></i>
                    Update Settings
                  </Button>
                </Card.Header>
                <Card.Body>
                  {paymentDetails ? (
                    <Row>
                      <Col xs={12} md={6} className="mb-3">
                        <Card className="bg-light h-100">
                          <Card.Body>
                            <h6 className="text-primary mb-3">
                              <i className="bi bi-phone me-2"></i>
                              UPI Details
                            </h6>
                            <div className="d-flex align-items-center">
                              <i className="bi bi-at text-muted me-2"></i>
                              <span>{paymentDetails.upiId}</span>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col xs={12} md={6} className="mb-3">
                        <Card className="bg-light h-100">
                          <Card.Body>
                            <h6 className="text-primary mb-3">
                              <i className="bi bi-bank me-2"></i>
                              Bank Details
                            </h6>
                            <div className="mb-2">
                              <small className="text-muted d-block">Bank Name</small>
                              <span>{paymentDetails.bankName || 'Not set'}</span>
                            </div>
                            <div className="mb-2">
                              <small className="text-muted d-block">Account Number</small>
                              <span>{paymentDetails.accountNumber || 'Not set'}</span>
                            </div>
                            <div className="mb-2">
                              <small className="text-muted d-block">IFSC Code</small>
                              <span>{paymentDetails.ifscCode || 'Not set'}</span>
                            </div>
                            <div>
                              <small className="text-muted d-block">Account Holder</small>
                              <span>{paymentDetails.accountHolderName || 'Not set'}</span>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  ) : (
                    <div className="text-center py-4">
                      <i className="bi bi-gear display-4 text-muted"></i>
                      <p className="text-muted mt-2">Payment details not configured</p>
                      <Button 
                        variant="primary"
                        onClick={() => setShowPaymentModal(true)}
                      >
                        Set Up Payment Details
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Custom Package Settings Tab */}
        {activeTab === 'custom-settings' && (
          <Row>
            <Col>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5>Custom Package Settings</h5>
                  <Button 
                    variant="primary"
                    onClick={() => setShowCustomSettingsModal(true)}
                  >
                    <i className="bi bi-gear me-2"></i>
                    Update Settings
                  </Button>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col xs={12} md={6} className="mb-3">
                      <Card className="bg-light h-100">
                        <Card.Body>
                          <h6 className="text-primary mb-3">
                            <i className="bi bi-calendar me-2"></i>
                            Per Schedule Base Price
                          </h6>
                          <div className="d-flex align-items-center">
                            <h4 className="text-success mb-0">₹{customSettings.perScheduleBasePrice}</h4>
                          </div>
                          <small className="text-muted">Price charged per schedule in custom packages</small>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col xs={12} md={6} className="mb-3">
                      <Card className="bg-light h-100">
                        <Card.Body>
                          <h6 className="text-primary mb-3">
                            <i className="bi bi-ticket me-2"></i>
                            Per Ticket Base Price
                          </h6>
                          <div className="d-flex align-items-center">
                            <h4 className="text-success mb-0">₹{customSettings.perTicketBasePrice}</h4>
                          </div>
                          <small className="text-muted">Price charged per ticket in custom packages</small>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  <Alert variant="info">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Note:</strong> These settings will be used for all new custom packages created by users. 
                    Changes will not affect existing packages.
                  </Alert>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Package Modal */}
        <Modal show={showPackageModal} onHide={() => setShowPackageModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editingPackage ? 'Edit Package' : 'Add New Package'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Package Name</Form.Label>
                <Form.Control
                  type="text"
                  value={packageForm.packageName}
                  onChange={(e) => setPackageForm({...packageForm, packageName: e.target.value})}
                />
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Schedule Count</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={packageForm.scheduleCount}
                      onChange={(e) => setPackageForm({...packageForm, scheduleCount: parseInt(e.target.value)})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Per Schedule Base Price</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={packageForm.perScheduleBasePrice}
                      onChange={(e) => setPackageForm({...packageForm, perScheduleBasePrice: parseFloat(e.target.value)})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tickets per Schedule</Form.Label>
                    <Form.Control
                      type="number"
                      min="50"
                      value={packageForm.ticketCountPerSchedule}
                      onChange={(e) => setPackageForm({...packageForm, ticketCountPerSchedule: parseInt(e.target.value)})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Per Ticket Base Price</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={packageForm.perTicketBasePrice}
                      onChange={(e) => setPackageForm({...packageForm, perTicketBasePrice: parseFloat(e.target.value)})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Restricted Package (Only Super Admin can assign)"
                  checked={packageForm.isRestricted}
                  onChange={(e) => setPackageForm({...packageForm, isRestricted: e.target.checked})}
                />
              </Form.Group>
              
              <Alert variant="info">
                <strong>Total Price: ₹{calculateTotalPrice()}</strong>
              </Alert>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPackageModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handlePackageSubmit}>
              {editingPackage ? 'Update' : 'Create'} Package
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Custom Package Settings Modal */}
        <Modal show={showCustomSettingsModal} onHide={() => setShowCustomSettingsModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Custom Package Settings</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Per Schedule Base Price</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="1000"
                  value={customSettings.perScheduleBasePrice}
                  onChange={(e) => setCustomSettings({...customSettings, perScheduleBasePrice: parseInt(e.target.value) || 100})}
                />
                <Form.Text className="text-muted">
                  Base price charged per schedule in custom packages (minimum: ₹1)
                </Form.Text>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Per Ticket Base Price</Form.Label>
                <Form.Control
                  type="number"
                  min="0.1"
                  max="50"
                  step="0.1"
                  value={customSettings.perTicketBasePrice}
                  onChange={(e) => setCustomSettings({...customSettings, perTicketBasePrice: parseFloat(e.target.value) || 2})}
                />
                <Form.Text className="text-muted">
                  Base price charged per ticket in custom packages (minimum: ₹0.1)
                </Form.Text>
              </Form.Group>
              
              <Alert variant="warning">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>Important:</strong> These changes will only apply to new custom packages. 
                Existing packages will retain their original pricing.
              </Alert>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCustomSettingsModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCustomSettingsSubmit}>
              Save Settings
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Payment Details Modal */}
        <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Payment Settings</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>UPI ID</Form.Label>
                <Form.Control
                  type="text"
                  value={paymentForm.upiId}
                  onChange={(e) => setPaymentForm({...paymentForm, upiId: e.target.value})}
                  placeholder="example@upi"
                />
              </Form.Group>
              
              <hr />
              <h6>Bank Details (Optional)</h6>
              
              <Form.Group className="mb-3">
                <Form.Label>Bank Name</Form.Label>
                <Form.Control
                  type="text"
                  value={paymentForm.bankName}
                  onChange={(e) => setPaymentForm({...paymentForm, bankName: e.target.value})}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Account Number</Form.Label>
                <Form.Control
                  type="text"
                  value={paymentForm.accountNumber}
                  onChange={(e) => setPaymentForm({...paymentForm, accountNumber: e.target.value})}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>IFSC Code</Form.Label>
                <Form.Control
                  type="text"
                  value={paymentForm.ifscCode}
                  onChange={(e) => setPaymentForm({...paymentForm, ifscCode: e.target.value})}
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Account Holder Name</Form.Label>
                <Form.Control
                  type="text"
                  value={paymentForm.accountHolderName}
                  onChange={(e) => setPaymentForm({...paymentForm, accountHolderName: e.target.value})}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handlePaymentDetailsSubmit}>
              Save Settings
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </PermissionWrapper>
  );
};

export default ManagePackagePage;